import React from 'react';
import { Button, ErrorState, Badge } from '@parsina/framework';
import { ArrowDown } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT } from '../../../i18n/I18nProvider.jsx';
import Odontogram from '../../../components/Odontogram/Odontogram.jsx';
import ToothDrawer from '../../../components/Odontogram/ToothDrawer.jsx';
import Panel from '../../../components/common/Panel.jsx';
import { OdontogramPanelSkeleton, ToothHistorySkeleton } from '../Visit.skeleton.jsx';
import { useToothHistory } from '../../../hooks/useDentalChart.js';
import { seedFromFinding } from '../../../lib/toothDraft.js';
import ToothHistory from '../../../components/Odontogram/ToothHistory.jsx';
import {
  outstandingPlanTeeth, labelFor, treatedByFdi, wholeMouthRecords, recordTitle,
} from '../Visit.shared.js';

// §3.1.1 — the odontogram, at the TOP of the persistent column.
//
// "Visual representation of the patient's persistent dental status", with
// clickable teeth, colour-coded conditions, and "real-time status updates when
// treatments are planned or completed".
//
// ── The chart itself is not reimplemented, and that is deliberate ───────────
// `Odontogram` and `ToothDrawer` are the SAME components چارت دندان renders, at
// the same size, in the same anatomical frame. CORE.md §9 makes the arch a
// safety invariant: it is drawn in the PATIENT's frame in both locales and is
// never forked, zoomed, mirrored or wrapped in a transform. A visit-specific
// copy would be a second chart that could disagree with the record's.
//
// ── What this section ADDS ─────────────────────────────────────────────────
// The plan overlay. §3.1.3 requires treatments "linked directly to specific
// teeth on the Odontogram", and §3.1.1 requires the chart to move when one is
// planned or completed. So a tooth carrying plan items is marked, selecting a
// tooth shows what is planned on it beside its recorded state, and the plan's
// own writes reload the chart — the two halves of the right column stay in step
// because they read the same tooth key, not because they message each other.

const PLAN_TONE = {
  proposed: { bg: CT.warnSoft, ink: CT.warnInk },
  accepted: { bg: CT.accentSoft, ink: CT.accentInk },
  done: { bg: CT.goodSoft, ink: CT.goodInk },
  rejected: { bg: CT.surface2, ink: C.textSecondary },
  deferred: { bg: CT.surface2, ink: C.textSecondary },
};

const Stat = ({ label, value, tone }) => (
  <div style={{
    background: CT.surface, border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '8px 12px', minWidth: 74,
  }}>
    <div style={{ fontSize: 10.5, color: C.textSecondary, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 800, color: tone || C.navy }} {...LTR_TEXT}>{value}</div>
  </div>
);

export default function VisitOdontogram({
  patientId, clinicId, t, formatNumber, formatDate,
  chart, planTeeth, selectedTooth, onSelectTooth, onPlanTooth, editable,
  // What has been RECORDED on this patient's teeth, and the catalogue that
  // names each one. The chart drew the PLAN and nothing else, so a treatment
  // the dentist had just recorded left no mark on the arch at all.
  records = [], catalog = [], appointmentId = null,
  /**
   * Every tooth the dentist CLICKS on the arch, as an event rather than as
   * state — «دندان‌های درگیر» toggles on it, and a toggle needs to hear the
   * second click on a tooth that is already selected, which a `selectedTooth`
   * that did not change cannot report.
   *
   * Fired only from a real click on the chart, never from the programmatic
   * selection a confirmed image finding makes: reviewing an AI finding is not
   * the dentist saying the complaint is about that tooth.
   */
  onToothPick,
  /**
   * A finding the dentist ticked on an image, waiting to be confirmed.
   *
   * It arrives from فایل‌ها — a different pane, two columns away — because the
   * decision made 2026-08-31 is that an AI finding PRE-FILLS this editor rather
   * than writing to the chart: a model that misread a tooth number would
   * otherwise put a filling on the wrong tooth, and the correction is a void
   * entry an append-only chart carries forever.
   *
   * Consumed once and cleared, so re-rendering this section does not reopen a
   * drawer the dentist just dismissed.
   */
  pendingFinding, onFindingConsumed,
}) {
  const { teeth, byFdi, summary, loading, error, saving, record, reload } = chart;
  const [focusSurface, setFocusSurface] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [seed, setSeed] = React.useState(null);
  // Which tooth «افزودن به طرح درمان» was last pressed for. The form it opens
  // lives two sections down the same scrolling column, so without a word here
  // the press reads as having done nothing at all. Named for what it IS — the
  // form opened — because the treatment itself is not chosen yet and claiming
  // "added" would be a confirmation of something that has not happened.
  const [planFormFor, setPlanFormFor] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const historyId = React.useId();

  // One read per tooth, and only once its trail is asked for.
  const history = useToothHistory({
    patientId,
    toothFdi: selectedTooth,
    enabled: showHistory && !!selectedTooth,
  });

  // Moving to another tooth closes the trail: leaving it open would show one
  // tooth's history under another tooth's heading for the frame before the new
  // read lands.
  React.useEffect(() => { setShowHistory(false); }, [selectedTooth]);

  // Same reasoning for the plan notice: it names a tooth, so it must not
  // survive onto a different one.
  React.useEffect(() => { setPlanFormFor(null); }, [selectedTooth]);

  // A finding ticked in فایل‌ها opens the editor here, pre-filled.
  //
  // The finding is consumed IMMEDIATELY — before the drawer has been closed or
  // saved — so that dismissing it does not leave a pending proposal that
  // reopens on the next unrelated re-render. The seed lives in this component's
  // own state from that point on, which is where a draft belongs.
  React.useEffect(() => {
    if (!pendingFinding) return;
    onSelectTooth?.(pendingFinding.toothFdi);
    setSeed(seedFromFinding(pendingFinding));
    setFocusSurface(pendingFinding.aspect === 'surface' ? pendingFinding.surface : null);
    setDrawerOpen(true);
    onFindingConsumed?.();
  }, [pendingFinding, onSelectTooth, onFindingConsumed]);

  const openTooth = React.useCallback((fdi, surface = null) => {
    onSelectTooth(fdi);
    onToothPick?.(fdi);
    setFocusSurface(surface);
    setDrawerOpen(true);
  }, [onSelectTooth, onToothPick]);

  // The teeth with OUTSTANDING work, marked on the arch. `Odontogram` takes a
  // `highlight` set rather than being taught about plans: it draws teeth, and
  // what a highlight MEANS is this screen's business.
  //
  // Outstanding, not "has any item": completing the last treatment on a tooth
  // must clear its mark, which is §3.1.1's "real-time status updates when
  // treatments are planned or COMPLETED". Keying on mere membership left a
  // permanent ring on every tooth ever planned, including declined lines.
  const plannedFdis = React.useMemo(() => outstandingPlanTeeth(planTeeth), [planTeeth]);

  // «امروز» / «همه» — in the chair the question is «what have I done today»,
  // and between visits it is «what has ever been done». Defaults to ALL,
  // because a chart that hid last month's crown would be lying about the tooth.
  const [scope, setScope] = React.useState('all');
  const scopedRecords = React.useMemo(() => (
    scope === 'visit' && appointmentId
      ? records.filter((r) => r?.appointmentId === appointmentId)
      : records
  ), [records, scope, appointmentId]);

  const treatedFdis = React.useMemo(
    () => treatedByFdi(scopedRecords, catalog), [scopedRecords, catalog],
  );
  const wholeMouth = React.useMemo(
    () => wholeMouthRecords(scopedRecords), [scopedRecords],
  );
  // The selected tooth's own treatments, for the merged history below.
  const toothRecords = React.useMemo(() => (
    selectedTooth ? records.filter((r) => r?.toothFdi === selectedTooth) : []
  ), [records, selectedTooth]);
  const selectedPlanItems = selectedTooth ? planTeeth.get(selectedTooth) || [] : [];

  // This panel alone, in its own shape — the stat strip, the arch at its
  // aspect, the hint strip — inside the real Panel, while شرح حال beside it is
  // already being typed into (DESIGN.md §"Loading & skeleton", region load).
  if (loading) {
    return <OdontogramPanelSkeleton t={t} label={t('common.loading')} />;
  }

  if (error) {
    return (
      <Panel title={t('visit.chart.title')}>
        <div role="alert">
          {/* `onRetry`, NOT `action`: the kit's ErrorState renders its button
              from `onRetry` and spreads anything else onto a <div>, so an
              `action` prop reached the DOM as an attribute and the chart's
              error state had no way back at all. Every other error state in
              this feature already used `onRetry`; this one was the outlier. */}
          <ErrorState
            title={t('chart.error.title')}
            description={t('chart.error.body')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title={t('visit.chart.title')}
      action={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Stat label={t('chart.summary.decayed')} value={formatNumber(summary?.decayed ?? 0)}
                tone={summary?.decayed ? CT.dangerInk : undefined} />
          <Stat label={t('chart.summary.missing')} value={formatNumber(summary?.missing ?? 0)} />
          <Stat label={t('chart.summary.dmft')} value={formatNumber(summary?.dmft ?? 0)} />
        </div>
      }
    >
      <div data-testid="visit-chart" data-state={teeth.length ? 'content' : 'empty'}>
        <Odontogram
          teeth={teeth}
          selected={selectedTooth}
          highlight={plannedFdis}
          treated={treatedFdis}
          planned={planTeeth}
          onSelect={(fdi) => openTooth(fdi)}
          onSurfaceSelect={(fdi, surface) => openTooth(fdi, surface)}
          showPrimary="auto"
          labelMode="fdi"
          t={t}
          formatDate={formatDate}
        />

        {/* ── «کل دهان» ────────────────────────────────────────────────────
            A جرم‌گیری belongs to no tooth. Marking all 32 would bury the teeth
            that actually had work under a wash of identical badges, so the
            whole-mouth treatments get one line beneath the arch instead — and
            they DO have to be somewhere, because they are real work that
            otherwise leaves no trace on this screen at all. */}
        {wholeMouth.length > 0 && (
          <div
            data-testid="chart-whole-mouth"
            style={{
              marginTop: 8, fontSize: 11.5, color: C.textSecondary,
              display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline',
            }}
          >
            <span style={{ color: C.textMuted }}>{t('chart.wholeMouth.title')}{t('common.punct.colon')}</span>
            {wholeMouth.slice(0, 3).map((r, i) => (
              <span key={r.id}>
                {i > 0 ? '· ' : ''}{recordTitle(r, catalog) || t('visit.plan.untitledItem')}
              </span>
            ))}
            {wholeMouth.length > 3 && (
              <span style={{ color: C.textMuted }}>
                {t('chart.tooltip.andMore', { count: formatNumber(wholeMouth.length - 3) })}
              </span>
            )}
          </div>
        )}

        {/* §3.1.1's "clickable teeth for drilling down into specific
            history/findings", answered inline as well as in the drawer: the
            drawer is a modal and the dentist's hands are busy, so what is
            planned on the selected tooth is readable without opening one. */}
        <div style={{
          marginTop: 10, padding: '10px 12px', background: CT.surface2, borderRadius: 10,
          minHeight: 44,
        }}>
          {!selectedTooth ? (
            <span style={{ fontSize: 12, color: C.textMuted }}>{t('visit.chart.hint')}</span>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <strong style={{ fontSize: 13, color: C.textPrimary }}>
                  {t('visit.chart.tooth', { fdi: formatNumber(selectedTooth) })}
                </strong>
                <Badge variant="outline">
                  {labelFor(t, 'chart.presence', byFdi[selectedTooth]?.presence || 'present')}
                </Badge>
                <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8 }}>
                  <Button variant="subtle" className="min-h-11" onClick={() => setDrawerOpen(true)}>
                    {t('visit.chart.openTooth')}
                  </Button>
                  {editable && (
                    <Button
                      variant="outline"
                      className="min-h-11"
                      onClick={() => {
                        onPlanTooth(selectedTooth);
                        setPlanFormFor(selectedTooth);
                      }}
                    >
                      {t('visit.chart.planForTooth')}
                    </Button>
                  )}
                </div>
              </div>

              {planFormFor === selectedTooth && (
                <div
                  role="status"
                  data-testid="chart-plan-form-opened"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: CT.accentSoft, borderRadius: 8,
                    padding: '6px 9px', marginBottom: 6,
                    fontSize: 11.5, fontWeight: 700, color: CT.accentInk,
                  }}
                >
                  <ArrowDown size={13} aria-hidden style={{ flexShrink: 0 }} />
                  <span>
                    {t('visit.chart.planFormOpened', { fdi: formatNumber(selectedTooth) })}
                  </span>
                </div>
              )}

              {selectedPlanItems.length === 0 ? (
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  {t('visit.chart.noPlanForTooth')}
                </span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedPlanItems.map((item) => {
                    const tone = PLAN_TONE[item.status] || PLAN_TONE.proposed;
                    return (
                      <span
                        key={item.itemId}
                        style={{
                          background: tone.bg, color: tone.ink, borderRadius: 8,
                          padding: '3px 9px', fontSize: 11.5, fontWeight: 700,
                        }}
                      >
                        {item.description || t('visit.plan.untitledItem')}
                        {' · '}
                        {labelFor(t, 'visit.plan.status', item.status)}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* §3.1.1's OTHER half — "drilling down into specific HISTORY".
                  The chart's folded state says a tooth is crowned; it does not
                  say when, by whom, or what was there before. Lazy: one read per
                  tooth, fired only when this is opened. */}
              <div style={{ marginTop: 8 }}>
                <Button
                  variant="subtle"
                  className="min-h-11"
                  aria-expanded={showHistory}
                  aria-controls={historyId}
                  onClick={() => setShowHistory((v) => !v)}
                >
                  {t('visit.chart.history')}
                </Button>

                {showHistory && (
                  <div id={historyId} style={{ marginTop: 8 }}>
                    {/* ONE list: what was DONE to this tooth interleaved with
                        what is TRUE of it. The events alone answered half the
                        question and left out the half carrying the who, the
                        visit, the session and the material — so a dentist read
                        «root_canal» with no way to see that they recorded it
                        themselves last month. */}
                    {history.loading
                      ? <ToothHistorySkeleton label={t('common.loading')} />
                      : (
                        <ToothHistory
                          fdi={selectedTooth}
                          events={history.events}
                          records={toothRecords}
                          catalog={catalog}
                          error={!!history.error}
                          t={t}
                          formatDate={formatDate}
                        />
                      )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ToothDrawer
        open={drawerOpen && !!selectedTooth}
        fdi={selectedTooth}
        state={selectedTooth ? byFdi[selectedTooth] : null}
        focusSurface={focusSurface}
        seed={seed}
        saving={saving}
        // A finished visit is read-only everywhere, and the chart is not an
        // exception: charting into yesterday's appointment is the same mistake
        // as writing a diagnosis into it.
        onSave={editable ? record : undefined}
        readOnly={!editable}
        onClose={() => { setDrawerOpen(false); setFocusSurface(null); setSeed(null); }}
        t={t}
      />
    </Panel>
  );
}
